import React, { useEffect, useRef, useState } from 'react';

const GKEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
let mapsPromise = null;

function loadGoogleMaps() {
  if (typeof window === 'undefined' || !GKEY) return Promise.resolve(null);
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (mapsPromise) return mapsPromise;
  // Also check if a Maps <script> is already in the DOM (e.g. StrictMode remount race)
  const existing = document.querySelector('script[data-iris-maps="1"]');
  if (existing) {
    mapsPromise = new Promise((resolve) => {
      existing.addEventListener('load', () => resolve(window.google));
      if (window.google?.maps?.places) resolve(window.google);
    });
    return mapsPromise;
  }
  mapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GKEY}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-iris-maps', '1');
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return mapsPromise;
}

/**
 * Google Places autocomplete input, biased to Dubai / UAE.
 * Falls back to a plain text input if the API key isn't configured.
 *
 * Props:
 *   value, onChange(str), onPlace(placeDetails)  — called with { name, address, lat, lng, place_id, types }
 *   placeholder, testid, className, biasCenter (optional {lat,lng})
 */
export default function PlacesAutocomplete({
  value, onChange, onPlace,
  placeholder = 'Search places, communities, buildings…',
  testid = 'places-autocomplete',
  className = '',
  biasCenter,
  autoPickOnBlur = false,
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const lastPickedRef = useRef('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!GKEY) { setReady(false); return; }
    let cancelled = false;
    loadGoogleMaps().then((g) => {
      if (cancelled || !g || !inputRef.current || autocompleteRef.current) return;
      // Bias to Dubai by default
      const bounds = new g.maps.LatLngBounds(
        new g.maps.LatLng(24.8, 54.8),
        new g.maps.LatLng(25.4, 55.6),
      );
      const ac = new g.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: ['ae'] },
        bounds,
        strictBounds: false,
        fields: ['name', 'formatted_address', 'geometry.location', 'place_id', 'types'],
      });
      ac.addListener('place_changed', () => {
        const p = ac.getPlace();
        if (!p) return;
        const details = {
          name: p.name,
          address: p.formatted_address,
          lat: p.geometry?.location?.lat?.(),
          lng: p.geometry?.location?.lng?.(),
          place_id: p.place_id,
          types: p.types,
        };
        lastPickedRef.current = details.name || details.address || '';
        onChange?.(details.name || details.address || '');
        onPlace?.(details);
      });
      autocompleteRef.current = ac;
      setReady(true);
    }).catch(() => setReady(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  const handleBlur = async () => {
    if (!autoPickOnBlur || !value || !ready) return;
    // If the user already selected a suggestion whose name matches the current value, skip.
    if (value === lastPickedRef.current) return;
    try {
      const g = window.google;
      if (!g?.maps?.places) return;
      const svc = new g.maps.places.AutocompleteService();
      const bounds = new g.maps.LatLngBounds(
        new g.maps.LatLng(24.8, 54.8),
        new g.maps.LatLng(25.4, 55.6),
      );
      const preds = await new Promise((resolve) => {
        svc.getPlacePredictions({
          input: value,
          componentRestrictions: { country: ['ae'] },
          bounds,
        }, (r) => resolve(r || []));
      });
      const top = preds?.[0];
      if (!top?.place_id) return;
      const detailsSvc = new g.maps.places.PlacesService(document.createElement('div'));
      const details = await new Promise((resolve) => {
        detailsSvc.getDetails({
          placeId: top.place_id,
          fields: ['name', 'formatted_address', 'geometry.location', 'place_id', 'types'],
        }, (r) => resolve(r));
      });
      if (!details?.geometry?.location) return;
      const picked = {
        name: details.name,
        address: details.formatted_address,
        lat: details.geometry.location.lat(),
        lng: details.geometry.location.lng(),
        place_id: details.place_id,
        types: details.types,
      };
      lastPickedRef.current = picked.name || picked.address || '';
      onChange?.(picked.name || picked.address || value);
      onPlace?.(picked);
    } catch { /* silent */ }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      onBlur={handleBlur}
      placeholder={ready || !GKEY ? placeholder : 'Loading places…'}
      className={className || 'w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm'}
      data-testid={testid}
    />
  );
}
